import { z } from 'zod';
import { UuidSchema } from './common.js';

export const WorkQueueSourceSchema = z.enum([
  'rank_drop',
  'gsc_decay',
  'gsc_opportunity',
  'competitor_gap',
  'audit_issue',
  'keyword_gap',
]);
export type WorkQueueSource = z.infer<typeof WorkQueueSourceSchema>;

export const WorkQueuePrioritySchema = z.enum(['critical', 'high', 'medium', 'low']);
export type WorkQueuePriority = z.infer<typeof WorkQueuePrioritySchema>;

export const WorkQueueCtaSchema = z.object({
  label: z.string(),
  /** App path the UI can navigate to (may include query/hash). */
  href: z.string(),
});
export type WorkQueueCta = z.infer<typeof WorkQueueCtaSchema>;

export const WorkQueueItemSchema = z.object({
  id: z.string(),
  source: WorkQueueSourceSchema,
  priority: WorkQueuePrioritySchema,
  /** Sort key — higher = more urgent. Opaque to clients; use for stable ordering. */
  score: z.number(),
  title: z.string(),
  why: z.string(),
  cta: WorkQueueCtaSchema,
  refs: z
    .object({
      project_id: UuidSchema.optional(),
      tracked_keyword_id: UuidSchema.optional(),
      audit_id: UuidSchema.optional(),
      issue_id: UuidSchema.optional(),
      analysis_id: UuidSchema.optional(),
      gap_id: UuidSchema.optional(),
      keyword_run_id: UuidSchema.optional(),
      keyword_id: UuidSchema.optional(),
      page_url: z.string().optional(),
      query: z.string().optional(),
    })
    .optional(),
});
export type WorkQueueItem = z.infer<typeof WorkQueueItemSchema>;

export const WorkQueueSectionMetaSchema = z.object({
  source: WorkQueueSourceSchema,
  available: z.boolean(),
  count: z.number().int().nonnegative(),
  /** Why the section was omitted or emptied (e.g. no completed audit). */
  omitted_reason: z.string().nullable(),
});
export type WorkQueueSectionMeta = z.infer<typeof WorkQueueSectionMetaSchema>;

export const WorkQueueResponseSchema = z.object({
  project_id: UuidSchema,
  generated_at: z.string(),
  items: z.array(WorkQueueItemSchema),
  sections: z.array(WorkQueueSectionMetaSchema),
});
export type WorkQueueResponse = z.infer<typeof WorkQueueResponseSchema>;

/** Cap on the ranked Monday-morning list. */
export const WORK_QUEUE_MAX_ITEMS = 15;

/** Per-source soft caps before merge (keeps the list diverse). */
export const WORK_QUEUE_SOURCE_CAPS = {
  rank_drop: 3,
  gsc_decay: 3,
  gsc_opportunity: 2,
  competitor_gap: 3,
  audit_issue: 3,
  keyword_gap: 2,
} as const;
